<script>
import lazyLoadComponent from '@router/lazyload-component'

export default {
  components: {
    ImageUpload: () => lazyLoadComponent(import('@views/chat/components/FileUpload')),
  },
  props: {
    defaultValue: {
      type: Object,
      default: () => ({}),
    },
    tagList: {
      type: Array,
      default: () => [],
    },
    categoryList: {
      type: Array,
      default: () => [],
    },
    productList: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      rules: {
        name: [{ required: true, message: '請輸入商品名稱', trigger: 'blur' }],
        categoryIds: [{ required: true, message: '請選擇寵物分類', trigger: 'change' }],
        productIds: [{ required: true, message: '請選擇適用分店', trigger: 'change' }],
        billingType: [{ required: true, message: '請選擇計價方式', trigger: 'change' }],
      },
    }
  },
  computed: {
    parentCategoryList() {
      return this.categoryList.filter((c) => c.children && c.children.length > 0)
    },
  },
  created() {
    if (!this.defaultValue.billingType) {
      this.$set(this.defaultValue, 'billingType', 'once')
    }
  },
  methods: {
    validate() {
      return this.$refs.form.validate()
    },
  },
}
</script>

<template>
  <el-form ref="form" :model="defaultValue" :rules="rules" label-position="top">
    <el-row :gutter="20">
      <!-- Row 1: 商品名稱 | 寵物分類 -->
      <el-col :sm="12" :span="24">
        <el-form-item label="商品名稱" prop="name">
          <el-input v-model.trim="defaultValue.name" placeholder="請輸入商品名稱" class="bg-white is-border" />
        </el-form-item>
      </el-col>

      <el-col :sm="12" :span="24">
        <el-form-item label="寵物分類" prop="categoryIds">
          <el-select v-model="defaultValue.categoryIds" multiple placeholder="選擇分類" style="width: 100%;" class="bg-white is-border">
            <el-option v-for="cat in parentCategoryList" :key="cat.id" :label="cat.title" :value="cat.id" />
          </el-select>
        </el-form-item>
      </el-col>

      <!-- Row 2: 適用分店 | 標籤 -->
      <el-col :sm="12" :span="24">
        <el-form-item label="適用分店" prop="productIds">
          <el-select v-model="defaultValue.productIds" multiple placeholder="選擇分店" style="width: 100%;" class="bg-white is-border">
            <el-option v-for="p in productList" :key="p.id" :label="p.name" :value="p.id" />
          </el-select>
        </el-form-item>
      </el-col>

      <el-col :sm="12" :span="24">
        <el-form-item label="標籤">
          <el-select v-model="defaultValue.tagIds" multiple placeholder="選擇標籤（選填）" style="width: 100%;" class="bg-white is-border">
            <el-option v-for="tag in tagList" :key="tag.id" :label="tag.name" :value="tag.id">
              <el-tag :type="tag.color || undefined" size="mini" style="margin-right: 6px;">
                {{ tag.name }}
              </el-tag>
            </el-option>
          </el-select>
        </el-form-item>
      </el-col>

      <!-- Row 3: 計價方式 -->
      <el-col :span="24">
        <el-form-item label="計價方式" prop="billingType">
          <div class="billingTypeGroup">
            <label class="billingTypeCard" :class="{ 'is-active': defaultValue.billingType === 'once' }" @click="$set(defaultValue, 'billingType', 'once')">
              <span class="billingTypeRadio" :class="{ 'is-checked': defaultValue.billingType === 'once' }" />
              <span class="billingTypeContent">
                <span class="billingTypeMain">單次</span>
                <span class="billingTypeDesc">單價 × 數量</span>
              </span>
            </label>
            <label class="billingTypeCard" :class="{ 'is-active': defaultValue.billingType === 'per_night' }" @click="$set(defaultValue, 'billingType', 'per_night')">
              <span class="billingTypeRadio" :class="{ 'is-checked': defaultValue.billingType === 'per_night' }" />
              <span class="billingTypeContent">
                <span class="billingTypeMain">每日</span>
                <span class="billingTypeDesc">單價 × 數量 × 住宿晚數</span>
              </span>
            </label>
          </div>
        </el-form-item>
      </el-col>

      <!-- Row 4: 封面圖 (enlarged) -->
      <el-col :span="24">
        <el-form-item label="封面圖">
          <div class="imageUploadWrap">
            <ImageUpload v-model="defaultValue.image" :show-upload="true" :resource="'product'" :single="true" :props-more-h-w="true" />
          </div>
        </el-form-item>
      </el-col>

      <!-- Row 4: 描述 (white background) -->
      <el-col :span="24">
        <el-form-item label="描述">
          <el-input v-model="defaultValue.description" type="textarea" :rows="4" placeholder="請輸入商品描述（選填）" class="bg-white is-border is-rounded" />
        </el-form-item>
      </el-col>
    </el-row>
  </el-form>
</template>

<style lang="scss" scoped>
.imageUploadWrap {
  ::v-deep .image-and-action,
  ::v-deep .el-upload--picture-card {
    margin-bottom: 0;
  }
}

.billingTypeGroup {
  display: flex;
  gap: 10px;

  @media (max-width: 767px) {
    flex-direction: column;
  }
}

.billingTypeCard {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 12px 18px;
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  flex: 1;

  @media (max-width: 767px) {
    flex: none;
    width: 100%;
  }

  &.is-active {
    border-color: #ea6a2a;
    background: rgba(234, 106, 42, 0.04);
    box-shadow: 0 0 0 1px #ea6a2a;
  }

  &:hover:not(.is-active) {
    border-color: #c0c4cc;
    background: #fafafa;
  }
}

.billingTypeRadio {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  min-width: 16px;
  border: 2px solid #dcdfe6;
  border-radius: 50%;
  background: #fff;
  flex-shrink: 0;
  position: relative;
  transition: border-color 0.15s;

  &::after {
    content: '';
    display: none;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #ea6a2a;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &.is-checked {
    border-color: #ea6a2a;
    &::after {
      display: block;
    }
  }
}

.billingTypeContent {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.billingTypeMain {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.billingTypeDesc {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}
</style>
